import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { CountBadge } from './Badge.js';
import { cx } from './cx.js';

export type TabItem<T extends string> = { id: T; label: ReactNode; count?: number };

/** Flèches gauche/droite, Début, Fin : déplacement entre onglets (motif WAI-ARIA). */
function useRovingKeys<T extends string>(items: TabItem<T>[], value: T, onChange: (v: T) => void) {
  const ref = useRef<HTMLDivElement>(null);
  const onKeyDown = (e: KeyboardEvent) => {
    const i = items.findIndex((t) => t.id === value);
    let next = -1;
    if (e.key === 'ArrowRight') next = (i + 1) % items.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + items.length) % items.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = items.length - 1;
    if (next < 0) return;
    e.preventDefault();
    const item = items[next];
    if (!item) return;
    onChange(item.id);
    ref.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
  };
  return { ref, onKeyDown };
}

/**
 * Onglets soulignés (fiche dossier). Défilent horizontalement sur téléphone, sans couper un libellé :
 * l'onglet choisi est ramené dans la vue, et un fondu sur le bord indique qu'il en reste à droite ou à
 * gauche. `sticky` les garde visibles en haut de l'écran pendant le défilement.
 */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
  label,
  sticky = false,
  className,
}: {
  items: TabItem<T>[];
  value: T;
  onChange: (v: T) => void;
  label: string;
  sticky?: boolean;
  className?: string;
}) {
  const { ref, onKeyDown } = useRovingKeys(items, value, onChange);
  const [edges, setEdges] = useState({ left: false, right: false });
  const measure = () => {
    const el = ref.current;
    if (!el) return;
    const left = el.scrollLeft > 4;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
    setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  };
  // L'onglet choisi est ramené dans la zone visible (horizontalement seulement : la page ne bouge pas).
  useEffect(() => {
    const el = ref.current;
    const tab = el?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
    if (el && tab && el.scrollWidth > el.clientWidth) {
      const start = tab.offsetLeft - 24;
      const end = tab.offsetLeft + tab.offsetWidth + 24;
      if (start < el.scrollLeft) el.scrollLeft = Math.max(0, start);
      else if (end > el.scrollLeft + el.clientWidth) el.scrollLeft = end - el.clientWidth;
    }
    measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, items.length]);
  const fade =
    edges.left && edges.right
      ? '[mask-image:linear-gradient(to_right,transparent,black_28px,black_calc(100%-28px),transparent)]'
      : edges.right
        ? '[mask-image:linear-gradient(to_right,black_calc(100%-28px),transparent)]'
        : edges.left
          ? '[mask-image:linear-gradient(to_right,transparent,black_28px)]'
          : undefined;
  return (
    <div
      ref={ref}
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      onScroll={measure}
      data-overflow={edges.right ? 'right' : edges.left ? 'left' : undefined}
      className={cx(
        'scroll-row flex gap-1 overflow-x-auto border-b border-line',
        sticky && 'sticky top-0 z-10 bg-canvas/92 backdrop-blur-md',
        fade,
        className,
      )}
    >
      {items.map((t) => {
        const selected = t.id === value;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(t.id)}
            className={cx(
              'relative flex min-h-(--ctl-lg) shrink-0 items-center gap-1.5 px-2.5 text-sm whitespace-nowrap transition-colors duration-150',
              'after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-full after:transition-colors',
              'focus-visible:-outline-offset-4',
              selected
                ? 'font-medium text-ink after:bg-brand-strong'
                : 'text-ink-3 after:bg-transparent hover:text-ink-2',
            )}
          >
            {t.label}
            {t.count ? <CountBadge count={t.count} /> : null}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Commande segmentée : deux à quatre vues d'un même contenu (ex. « Véhicules présents » /
 * « Rechercher un dossier »). Rôle `tablist` : un seul segment sélectionné.
 */
export function SegmentedControl<T extends string>({
  items,
  value,
  onChange,
  label,
  className,
}: {
  items: TabItem<T>[];
  value: T;
  onChange: (v: T) => void;
  label: string;
  className?: string;
}) {
  const { ref, onKeyDown } = useRovingKeys(items, value, onChange);
  return (
    <div
      ref={ref}
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className={cx('grid gap-1 rounded-lg bg-sunken p-0.5 ring-1 ring-inset ring-line', className)}
      style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
    >
      {items.map((t) => {
        const selected = t.id === value;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(t.id)}
            className={cx(
              'flex min-h-(--ctl-sm) items-center justify-center gap-1.5 rounded-md px-3 text-sm transition-[background-color,color,box-shadow] duration-150',
              selected ? 'bg-surface font-medium text-ink shadow-xs ring-1 ring-line' : 'text-ink-2 hover:text-ink',
            )}
          >
            {t.label}
            {t.count ? <CountBadge count={t.count} /> : null}
          </button>
        );
      })}
    </div>
  );
}
